'use client';

import React, { useEffect, useRef, type JSX } from 'react';
import { initMiniSentry } from './minisentry';
import type { PropsWithChildren } from './react-compat';
import Branding from './branding/BrandingTag';
import AnalyticsTracker from './analytics/AnalyticsTracker';

export interface DevtoolsProviderProps {
	inspector?: {
		highlightColor?: string;
		highlightWidth?: number;
		containerSelector?: string;
	};
	sentry?: Parameters<typeof initMiniSentry>[0];
	hideBranding?: boolean;
}

export function DevtoolsProvider(
	{ children, inspector, sentry, hideBranding = false }: PropsWithChildren<DevtoolsProviderProps>,
): JSX.Element {
	/* install Mini-Sentry exactly once */
	useEffect(() => {
		initMiniSentry(sentry ?? {});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/* ---- lazy-load the DOM inspector only on the client ---- */
	const InspectorRef = useRef<React.ComponentType<any> | null>(null);

	if (typeof window !== 'undefined' && InspectorRef.current === null) {
		// `require` happens only in the browser bundle
		InspectorRef.current =
			// eslint-disable-next-line @typescript-eslint/no-var-requires
			require('./inspector/DOMInspector').DOMInspector;
	}

	const Inspector = InspectorRef.current;

	if (Inspector) {
		return (
			<>
				<AnalyticsTracker />
				{!hideBranding && <Branding />}
				<Inspector {...inspector}>
					{children}
				</Inspector>
			</>
		);
	}
	return (
		<>
			<AnalyticsTracker />
			{!hideBranding && <Branding />}
			{children}
		</>
	);
}